import { useState, useCallback } from 'react';
import { useWeb3React } from '@web3-react/core';
import { getContract } from '../config/contracts';

export interface NFTMetadata {
  name?: string;
  description?: string;
  image?: string;
  attributes?: Array<{
    trait_type: string;
    value: string | number;
  }>;
  metadata?:any
}

export interface NFTItem {
  tokenId: string;
  contractAddress: string;
  owner: string;
  metadata?: NFTMetadata;
  tokenUri?: string;
  transferTime?: string;
}

export interface NFTPaginationResult {
  nfts: NFTItem[];
  pageKey?: string;
  totalCount?: number;
}

export interface UseNFTReturn {
  // Data status
  nfts: NFTItem[];
  isLoading: boolean;
  error: string | null;
  
  // Action functions
  loadNFTs: (pageKey?: string, pageSize?: number) => Promise<NFTPaginationResult | null>;
  loadNFTMetadata: (contractAddress: string, tokenId: string) => Promise<NFTMetadata | null>;
  refreshNFTs: () => Promise<void>;
}

const ALCHEMY_API_KEY = process.env.REACT_APP_ALCHEMY_API_KEY;
console.log('ALCHEMY_API_KEY:', ALCHEMY_API_KEY ? 'exists' : 'missing');

export const useNFT = (contractAddress?: string): UseNFTReturn => {
  const { chainId } = useWeb3React();
  const [nfts, setNfts] = useState<NFTItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getChainName = useCallback(() => {
    switch (chainId) {
      case 137:
        return 'polygon-mainnet';
      case 80002:
        return 'polygon-amoy';
      default:
        return 'polygon-mainnet';
    }
  }, [chainId]);

  const loadNFTs = useCallback(async (
    pageKey?: string, 
    pageSize: number = 20
  ): Promise<NFTPaginationResult | null> => {
    if (!contractAddress) {
      // If there is no contract address, no error is displayed, keep silent
      return null;
    }
    
    if (!ALCHEMY_API_KEY) {
      setError('API key is not configured');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const chain = getChainName();
      const apiUrl = `https://${chain}.g.alchemy.com/nft/v3/${ALCHEMY_API_KEY}`;
      const requestUrl = new URL(apiUrl + '/getNFTsForContract');
      
      requestUrl.searchParams.set('contractAddress', contractAddress);
      if (pageKey) {
        requestUrl.searchParams.set('startToken', pageKey);
      }
      requestUrl.searchParams.set('limit', pageSize.toString());
      requestUrl.searchParams.set('orderBy', 'transferTime');
      requestUrl.searchParams.set('withMetadata', 'true');

      const response = await fetch(requestUrl.toString());
      
      if (!response.ok) {
        throw new Error('NFT data service is temporarily unavailable, please try again later');
      }

      const data = await response.json();
      
      const nftItems: NFTItem[] = data.nfts?.map((nft: any) => ({
        tokenId: nft.tokenId,
        contractAddress: nft.contract?.address || contractAddress,
        owner: nft.mint?.mintAddress || nft.owners?.[0] || '',
        metadata: {
          name: nft.name || nft.raw?.metadata?.name,
          description: nft.description || nft.raw?.metadata?.description,
          image: nft.image?.cachedUrl || nft.image?.originalUrl || nft.raw?.metadata?.image,
          attributes: nft.raw?.metadata?.attributes,
        },
        tokenUri: nft.tokenUri || nft.raw?.tokenUri,
        transferTime: nft.timeLastUpdated,
      })) || [];

      setNfts(prev => pageKey ? [...prev, ...nftItems] : nftItems);
      
      return {
        nfts: nftItems,
        pageKey: data.pageKey,
        totalCount: data.totalCount,
      };
    } catch (err: any) {
      console.error('Failed to load NFT:', err);
      const errorMessage = err.message.includes('API') ? 'NFT data service is temporarily unavailable, please try again later' : 'Failed to load NFT data, please check the network connection';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [contractAddress, getChainName]);

  const loadNFTMetadata = useCallback(async (
    contract: string, 
    tokenId: string
  ): Promise<NFTMetadata | null> => {
    if (!ALCHEMY_API_KEY) {
      setError('NFT data service configuration is abnormal, please contact the administrator');
      return null;
    }

    try {
      const chain = getChainName();
      const apiUrl = `https://${chain}.g.alchemy.com/nft/v3/${ALCHEMY_API_KEY}`;
      const requestUrl = new URL(apiUrl + '/getNFTMetadata');
      
      requestUrl.searchParams.set('contractAddress', contract);
      requestUrl.searchParams.set('tokenId', tokenId);

      const response = await fetch(requestUrl.toString());
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
       //  console.error('加载NFT元数据data:', data);
      return {
        name: data.name || data.raw?.metadata?.name,
        description: data.description || data.raw?.metadata?.description,
        image: data.image?.cachedUrl || data.image?.originalUrl || data.raw?.metadata?.image,
        attributes: data.raw?.metadata?.attributes,
        metadata:data.raw?.metadata
      };
    } catch (err: any) {
      console.error('Failed to load NFT metadata:', err);
      const errorMessage = err.message.includes('NFT data service') ? err.message : 'Failed to load NFT details, please try again later';
      setError(errorMessage);
      return null;
    }
  }, [getChainName]);

  const refreshNFTs = useCallback(async () => {
    setNfts([]);
    await loadNFTs();
  }, [loadNFTs]);

  return {
    nfts,
    isLoading,
    error,
    loadNFTs,
    loadNFTMetadata,
    refreshNFTs,
  };
};

export default useNFT;